import { useState, useEffect, useRef, useCallback } from 'react';

const ESPERA_MAXIMA_MS = 5 * 60 * 1000;

// Ejecuta `tarea` al montar y luego cada `intervaloMs`, con estas reglas:
// - Nunca hay dos ejecuciones a la vez: el siguiente turno se agenda cuando
//   termina el anterior (setTimeout, no setInterval). Si se pide refrescar
//   mientras una está en curso, se repite apenas termine.
// - Con la pestaña oculta no consulta; al volver, consulta de inmediato.
// - Si la tarea falla, la espera se duplica (hasta 5 min) y vuelve al
//   intervalo normal con el primer acierto.
// `tarea` puede cambiar entre renders; siempre se usa la última.
export default function usePolling(tarea, intervaloMs) {
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [actualizando, setActualizando] = useState(false);
  const [fallos, setFallos] = useState(0);

  const tareaRef = useRef(tarea);
  tareaRef.current = tarea;
  const temporizador = useRef(null);
  const enCurso = useRef(false);
  const pendiente = useRef(false);
  const fallosRef = useRef(0);
  const montado = useRef(true);

  const ejecutar = useCallback(async () => {
    clearTimeout(temporizador.current);
    if (enCurso.current) {
      pendiente.current = true;
      return;
    }
    enCurso.current = true;
    setActualizando(true);
    try {
      await tareaRef.current();
      fallosRef.current = 0;
      if (montado.current) setUltimaActualizacion(new Date());
    } catch {
      fallosRef.current += 1;
    } finally {
      enCurso.current = false;
      if (montado.current) {
        setActualizando(false);
        setFallos(fallosRef.current);
        if (pendiente.current) {
          pendiente.current = false;
          temporizador.current = setTimeout(ejecutar, 0);
        } else if (!document.hidden) {
          const espera = Math.min(intervaloMs * 2 ** fallosRef.current, ESPERA_MAXIMA_MS);
          temporizador.current = setTimeout(ejecutar, espera);
        }
      }
    }
  }, [intervaloMs]);

  useEffect(() => {
    montado.current = true;
    ejecutar();
    const alCambiarVisibilidad = () => {
      if (document.hidden) clearTimeout(temporizador.current);
      else ejecutar();
    };
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      montado.current = false;
      clearTimeout(temporizador.current);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [ejecutar]);

  return { ultimaActualizacion, actualizando, fallos, refrescar: ejecutar };
}
